import { getUniqueId } from "@jugl-web/utils";
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EnqueueOverlayConfig,
  OverlayQueueContext,
  OverlayQueueContextValue,
  OverlayRenderer,
} from "./OverlayQueueContext";

interface OverlayQueueEntry {
  id: string;
  namespace?: string;
  renderer: OverlayRenderer;
}

interface OverlayQueueProviderProps {
  children: ReactNode;
}

/**
 * Provider that manages a queue of overlays (toasts/modals/popups) to display them sequentially.
 * Overlays are displayed one at a time in FIFO order.
 * Optionally, use namespaces to check if a specific overlay is open or queued.
 */
export const OverlayQueueProvider: FC<OverlayQueueProviderProps> = ({
  children,
}) => {
  const [queue, setQueue] = useState<OverlayQueueEntry[]>([]);

  const [isOpen, setIsOpen] = useState(false);

  const currentEntry = queue[0] ?? null;

  const enqueue = useCallback((config: EnqueueOverlayConfig): void => {
    const { namespace, renderer } = config;
    const id = getUniqueId();

    setQueue((prevState) => [...prevState, { id, namespace, renderer }]);
  }, []);

  const dequeue = useCallback(() => {
    setQueue((prevState) => {
      if (prevState.length === 0) {
        return prevState;
      }

      return prevState.slice(1);
    });
  }, []);

  const isOpenFn = useCallback(
    (namespace: string) => isOpen && currentEntry?.namespace === namespace,
    [isOpen, currentEntry?.namespace]
  );

  const isEnqueued = useCallback(
    (namespace: string) => queue.some((entry) => entry.namespace === namespace),
    [queue]
  );

  const replaceCurrent = useCallback(
    (config: EnqueueOverlayConfig) => {
      if (queue.length === 0) {
        return;
      }

      setQueue((prevState) =>
        prevState.map((entry, index) => {
          if (index === 0) {
            return { ...entry, ...config };
          }

          return entry;
        })
      );
    },
    [queue.length]
  );

  const handleClose = useCallback(() => setIsOpen(false), []);

  const handleAfterClose = useCallback(() => dequeue(), [dequeue]);

  useEffect(() => {
    if (currentEntry) {
      setIsOpen(true);
    }
  }, [currentEntry]);

  const contextValue = useMemo<OverlayQueueContextValue>(
    () => ({
      enqueueOverlay: enqueue,
      replaceCurrentOverlay: replaceCurrent,
      isOverlayOpen: isOpenFn,
      isOverlayEnqueued: isEnqueued,
    }),
    [enqueue, isEnqueued, isOpenFn, replaceCurrent]
  );

  return (
    <OverlayQueueContext.Provider value={contextValue}>
      {children}
      {currentEntry && (
        <>
          {currentEntry.renderer({
            isOpen,
            onClose: handleClose,
            onAfterClose: handleAfterClose,
          })}
        </>
      )}
    </OverlayQueueContext.Provider>
  );
};
